import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function PersonalBusinessFinancesArticle() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = 'Why We Look at Your Personal Finances Too | Revivacorp';
    const metas: { property?: string; name?: string; content: string }[] = [
      { property: 'og:title', content: 'The Hidden Trap Killing Small Businesses: Commingling Personal & Business Finances' },
      { property: 'og:description', content: 'At Revivacorp, we\'ve seen it time and again — struggling small business owners mixing personal and business accounts. Here\'s why we address both, and why it matters for your recovery.' },
      { property: 'og:image', content: 'https://static.readdy.ai/image/a9d6aff03562300b57162884cb61551b/7fbf09dbcfaa601672c5e7d5615b1c01.png' },
      { property: 'og:url', content: 'https://revivacorp.com/resources/personal-business-finances' },
      { property: 'og:type', content: 'article' },
      { property: 'og:site_name', content: 'Revivacorp' },
      { property: 'article:author', content: 'Revivacorp' },
      { property: 'article:published_time', content: '2025-07-14' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'The Hidden Trap Killing Small Businesses: Commingling Personal & Business Finances' },
      { name: 'twitter:description', content: 'At Revivacorp, we\'ve seen it time and again — struggling small business owners mixing personal and business accounts. Here\'s why we address both.' },
      { name: 'twitter:image', content: 'https://static.readdy.ai/image/a9d6aff03562300b57162884cb61551b/7fbf09dbcfaa601672c5e7d5615b1c01.png' },
      { name: 'description', content: 'At Revivacorp, we\'ve seen it time and again — struggling small business owners mixing personal and business accounts. Here\'s why we address both, and why it matters for your recovery.' },
    ];

    const inserted: HTMLMetaElement[] = [];
    metas.forEach(({ property, name, content }) => {
      const el = document.createElement('meta');
      if (property) el.setAttribute('property', property);
      if (name) el.setAttribute('name', name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      inserted.push(el);
    });

    return () => {
      inserted.forEach((el) => document.head.removeChild(el));
      document.title = 'Revivacorp \u2013 Business Turnaround & Restructuring Consulting';
    };
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'The Hidden Trap Killing Small Businesses: Commingling Personal & Business Finances',
      description: 'At Revivacorp, we\'ve seen it time and again — struggling small business owners mixing personal and business accounts. Here\'s why we address both, and why it matters for your recovery.',
      image: 'https://static.readdy.ai/image/a9d6aff03562300b57162884cb61551b/7fbf09dbcfaa601672c5e7d5615b1c01.png',
      url: `${siteUrl}/resources/personal-business-finances`,
      datePublished: '2025-07-14',
      dateModified: '2025-07-14',
      author: {
        '@type': 'Organization',
        name: 'Revivacorp Advisory Team'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Revivacorp',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/resources/personal-business-finances`
      },
      articleSection: 'Financial Management',
      wordCount: 1600,
      keywords: 'commingling finances, small business finances, personal business separation, financial management, business turnaround'
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Resources',
          item: `${siteUrl}/resources`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Personal & Business Finances',
          item: `${siteUrl}/resources/personal-business-finances`
        }
      ]
    };

    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.text = JSON.stringify(articleSchema);
    document.head.appendChild(articleScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(articleScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero */}
      <section className="relative h-[520px] flex items-end overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/5f3f929fe0ab3bc48a135a4b3e8ceccc.jpg"
          alt="Personal and business finances commingled"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 lg:px-8 pb-14">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Resources
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">Financial Management</span>
            <span className="text-white/70 text-sm">July 14, 2025</span>
            <span className="text-white/70 text-sm">&bull; 7 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight max-w-3xl">
            The Hidden Trap Killing Small Businesses: Commingling Personal &amp; Business Finances
          </h1>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">

          {/* Author / Share Bar */}
          <div className="flex items-center justify-between mb-12 pb-8 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-500 text-white font-bold text-sm">R</div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Revivacorp Advisory Team</p>
                <p className="text-xs text-slate-500">Business Turnaround &amp; Restructuring Specialists</p>
              </div>
            </div>
            <a
              href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Frevivacorp.com%2Fresources%2Fpersonal-business-finances"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] text-white text-sm font-semibold rounded-md hover:bg-[#004182] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-fill text-base"></i>
              Share on LinkedIn
            </a>
          </div>

          {/* Intro */}
          <p className="text-lg text-slate-700 leading-relaxed mb-8 font-medium">
            When a struggling small business owner walks through our door, we almost always find the same thing hiding in plain sight — their personal and business finances are completely tangled together. One bank account. One credit card. Rent paid from the business. Payroll covered by a personal loan. It's one of the most common — and most damaging — patterns we see.
          </p>

          <p className="text-base text-slate-600 leading-relaxed mb-8">
            At Revivacorp, we don't just look at your business finances. We look at the full picture — and that includes your personal financial situation. Here's why that matters, and what we've learned from working with hundreds of small business owners in distress.
          </p>

          {/* Section 1 */}
          <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">What Is Commingling — and Why Is It So Common?</h2>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            Commingling simply means mixing personal and business money. It happens when an owner uses their personal checking account to pay a vendor, or pulls cash from the business to cover a mortgage payment. It's not always intentional — in fact, it rarely is.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            Most small business owners start their companies with personal savings. They open a business account eventually, but old habits stick. When cash gets tight, the line between "my money" and "the business's money" blurs fast. Before long, there's no clear line at all.
          </p>
          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-6 mb-8">
            <p className="text-teal-800 font-medium text-base leading-relaxed">
              "In over 80% of the distressed small businesses we've worked with, commingling was a contributing factor — not just a symptom, but an accelerant of the financial crisis."
            </p>
            <p className="text-teal-600 text-sm mt-2">— Revivacorp Advisory Team</p>
          </div>

          {/* Section 2 */}
          <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">Why It's So Destructive</h2>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            Commingling doesn't just create accounting headaches — it creates a financial blind spot that makes it nearly impossible to diagnose what's actually wrong with the business. When personal and business cash flows are mixed, you can't accurately measure profitability, you can't identify where money is leaking, and you can't make sound decisions about the future.
          </p>

          <ul className="space-y-4 mb-8">
            {[
              { icon: 'ri-error-warning-line', title: 'You lose legal protection', desc: 'Commingling can pierce the corporate veil, exposing your personal assets to business creditors and lawsuits.' },
              { icon: 'ri-bar-chart-line', title: 'Your financials become unreliable', desc: 'Lenders, investors, and advisors can\'t trust numbers that are contaminated with personal transactions.' },
              { icon: 'ri-search-eye-line', title: 'Tax exposure increases', desc: 'The IRS views commingled accounts as a red flag. Deductions become harder to defend, and audits become more likely.' },
              { icon: 'ri-mental-health-line', title: 'Personal financial stress bleeds in', desc: 'When your personal finances are struggling, you unconsciously make business decisions to solve personal problems — often at the business\'s expense.' },
            ].map((item) => (
              <li key={item.title} className="flex gap-4 items-start">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm mb-1">{item.title}</p>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Section 3 */}
          <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">Why We Look at Both — Business and Personal</h2>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            This is where Revivacorp's approach differs from a traditional business consultant or accountant. We don't just analyze your P&amp;L and balance sheet. We sit down and look at your personal financial picture too — your personal debt load, your household cash flow, your personal credit, and how your personal financial stress is influencing your business decisions.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            Why? Because you can't fix the business without understanding the owner. In many cases, the business isn't the only thing that needs a turnaround — the owner's personal finances do too. And if we only address one side, the other side will drag everything back down.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {[
              { label: 'Business Side', items: ['Cash flow analysis', 'Debt restructuring', 'Operational cost review', 'Creditor negotiations', 'Revenue recovery planning'] },
              { label: 'Personal Side', items: ['Personal debt assessment', 'Household cash flow review', 'Personal credit evaluation', 'Separation of finances', 'Personal financial stability plan'] },
            ].map((col) => (
              <div key={col.label} className="bg-slate-50 rounded-lg p-6">
                <p className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wide">{col.label}</p>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-base"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Section 4 */}
          <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-12">The Path Forward: Separation and Clarity</h2>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            The first step in any turnaround engagement we take on is establishing a clear separation between personal and business finances. This isn't just a bookkeeping exercise — it's a mindset shift. When an owner can clearly see what the business earns, what it spends, and what it owes — separate from their personal life — they can finally make clear-headed decisions.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            We help owners set up proper business banking, establish a reasonable owner's draw, and create a personal budget that doesn't rely on the business as a piggy bank. At the same time, we work on the business side to stabilize cash flow, renegotiate obligations, and build a sustainable path forward.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-10">
            The result? Owners who feel in control for the first time in years — because they finally have clarity on both sides of their financial life.
          </p>

          {/* Closing CTA */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-500 rounded-xl p-8 text-center mt-12">
            <h3 className="text-2xl font-bold text-white mb-3">Is This Your Situation?</h3>
            <p className="text-white/90 text-base mb-6 max-w-xl mx-auto">
              If you recognize your business in this article, you're not alone — and it's not too late. Let's have a conversation about both sides of your financial picture.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-teal-600 font-semibold rounded-full hover:bg-slate-50 transition-all shadow-md whitespace-nowrap cursor-pointer"
            >
              Schedule a Free Consultation
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Bottom Share */}
          <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">Found this helpful? Share it with someone who needs it.</p>
            <a
              href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Frevivacorp.com%2Fresources%2Fpersonal-business-finances"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] text-white text-sm font-semibold rounded-md hover:bg-[#004182] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-fill text-base"></i>
              Share on LinkedIn
            </a>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
